import AbstractAnalyticProvider from '../AbstractAnalyticProvider';
import EventModelFactory from './EventModelFactory';
import ManagerRepository from 'coreClasses/ManagerRepository';
import {EAnalyticsProvider} from 'coreClasses/analytics/IAnalytics';
import {EAlgoliaManagerEventType, EAlgoliaManagerEventName} from './IAlgoliaManager';
import {EEventType, IEventData} from 'coreClasses/events/IEvents';
import {getConfig} from 'coreUtils/utils';
import aa from 'search-insights';

/**
 * Represents AlgoliaManager
 */
class AlgoliaManager extends AbstractAnalyticProvider {
	protected eventModelFactory: EventModelFactory;
	protected managerRepository: ManagerRepository;
	protected lastClickedSearchHitId: string;
	protected lastSearchQueryID: string;

	/**
	 * Initializes Algolia Analytics module with the provided parameters.
	 * The configuration retrieved from the configuration manager.
	 * @returns {void}
	 */
	protected initialize() {
		const configurationMgr = this.managerRepository.getConfigurationsMgr();

		aa('init', {
			appId: configurationMgr.get('sitePreferences.Algolia_ApplicationID'),
			apiKey: configurationMgr.get('sitePreferences.Algolia_SearchApiKey'),
			useCookie: true
		});
	}

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super();

		this.eventModelFactory = new EventModelFactory(managerRepository);
		this.managerRepository = managerRepository;
		this.lastClickedSearchHitId = '';
		this.lastSearchQueryID = '';

		this.initialize();
	}

	/**
	 * Tracks analytics event
	 * @param {EEventType} eventType - event type
	 * @param {IEventData} eventData - event data
	 * @returns {void}
	 */
	update(eventType: EEventType, eventData: IEventData): void {

		if (eventData?.options?.excludeFromAnalytics?.includes(EAnalyticsProvider.ALGOLIA)) {
			return;
		}

		const algoliaEventType = EAlgoliaManagerEventType[EEventType[eventType]];
		const algoliaEventData: IEventData = {
			eventName: EAlgoliaManagerEventName[EEventType[eventType]],
			...eventData
		};

		if (algoliaEventType === EAlgoliaManagerEventType.PRODUCT_SUGGESTION_CLICK) {
			// Search query ID and last clicked search hit should be saved to use it in add to cart event
			this.setLastSearchQueryID(eventData?.searchQueryID);
			this.setLastClickedSearchHitId(eventData?.productData?.productID);
		} else if (algoliaEventType === EAlgoliaManagerEventType.PRODUCT_ADD_TO_CART) {
			if (eventData?.productItem?.c_variationGroupId !== this.getLastClickedSearchHitId()) {
				return;
			}

			algoliaEventData.searchQueryID = this.getLastSearchQueryID();
			algoliaEventData.productID = this.getLastClickedSearchHitId();
		}

		if (!algoliaEventData?.searchQueryID) {
			return;
		}

		algoliaEventData.indexName = this.getAlgoliaIndexName();

		const algoliaEvent = this.eventModelFactory.create(algoliaEventType, algoliaEventData);

		if (algoliaEvent === null) {
			return;
		}

		this.sendEvent(algoliaEventType, algoliaEvent);
	}

	/**
	 * Sets the ID of the last clicked search hit
	 * @param {string | undefined} searchHitId - The ID of the search hit to set, or undefined if not available.
	 * @returns {void}
	 */
	protected setLastClickedSearchHitId(searchHitId) {
		if (searchHitId) {
			this.lastClickedSearchHitId = searchHitId;
		}
	}

	/**
	 * Gets the ID of the last clicked search hit
	 * @returns {string} - The ID of the last clicked search hit
	 */
	protected getLastClickedSearchHitId() {
		return this.lastClickedSearchHitId;
	}

	/**
	 * Sets the last Algolia search query ID
	 * @param {string | undefined} queryID - The search query ID to set, or undefined if not available.
	 * @returns {void}
	 */
	protected setLastSearchQueryID(queryID) {
		if (queryID) {
			this.lastSearchQueryID = queryID;
		}
	}

	/**
	 * Gets the last Algolia search query ID
	 * @returns {string} - The last search query ID
	 */
	protected getLastSearchQueryID() {
		return this.lastSearchQueryID;
	}

	/**
	 * Gets the Algolia index name based on the provided type.
	 * @param {string} type - The type for which the Algolia index name is generated
	 * @returns {string} - The Algolia index name
	 */
	protected getAlgoliaIndexName(type: string = 'products'): string {
		const {app} = getConfig();
		const currentLanguage = this.managerRepository.getLocalesMgr().getCurrentLanguage()?.getLanguageCode() || '';
		const realmHostName = app.sfccHostname.replace(/[.|-]/g, '_');
		const name = `${realmHostName}__${app.sfccAPI.siteId}__${type}__${currentLanguage}`;

		return name;
	}

	/**
	 * Pushes an event to the Algolia
	 * @param {string} eventType - The type of the event
	 * @param {EventModel} event - The event object containing data
	 * @returns {void}
	 */
	protected sendEvent(eventType, event): void {
		aa(eventType, {
			...event?.data
		});
	}

}

export default AlgoliaManager;
